import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ErrorApi,
  actualizarMascota,
  crearMascota,
  obtenerCatalogos,
  obtenerMascota,
  type Catalogos,
  type GuardarMascota,
} from "../api";
import Campo from "../componentes/Campo";
import { paraInputFecha } from "../formato";

// Espejo de los enums del dominio. Si cambian allá, cambian aquí.
const SEXO = [
  { valor: 0, nombre: "Desconocido" },
  { valor: 1, nombre: "Macho" },
  { valor: 2, nombre: "Hembra" },
];

const ESTADO = [
  { valor: 1, nombre: "Activa" },
  { valor: 2, nombre: "Fallecida" },
  { valor: 3, nombre: "Perdida" },
  { valor: 4, nombre: "Entregada" },
];

const VACIA: GuardarMascota = {
  nombre: "",
  especieID: 0,
  razaID: null,
  colorID: null,
  sexo: 0,
  estado: 1,
  fechaNacimiento: null,
  lugarNacimiento: null,
  numeroChip: null,
  esterilizada: null,
  fechaEsterilizacion: null,
  fechaFallecimiento: null,
  alergias: null,
  observaciones: null,
  pesoKg: null,
};

export default function MascotaForm() {
  const { mascotaID } = useParams();
  const esEdicion = Boolean(mascotaID);

  const catalogos = useQuery({ queryKey: ["catalogos"], queryFn: obtenerCatalogos });
  const existente = useQuery({
    queryKey: ["mascota", mascotaID],
    queryFn: () => obtenerMascota(mascotaID!),
    enabled: esEdicion,
  });

  if (catalogos.isPending || (esEdicion && existente.isPending)) {
    return <p className="muted">Cargando…</p>;
  }
  if (catalogos.error) return <p className="err">{catalogos.error.message}</p>;
  if (existente.error) return <p className="err">{existente.error.message}</p>;

  const inicial: GuardarMascota = existente.data
    ? {
        nombre: existente.data.nombre,
        especieID: existente.data.especieID,
        razaID: existente.data.razaID,
        colorID: existente.data.colorID,
        sexo: existente.data.sexo,
        estado: existente.data.estado,
        fechaNacimiento: existente.data.fechaNacimiento,
        lugarNacimiento: existente.data.lugarNacimiento,
        numeroChip: existente.data.numeroChip,
        esterilizada: existente.data.esterilizada,
        fechaEsterilizacion: existente.data.fechaEsterilizacion,
        fechaFallecimiento: existente.data.fechaFallecimiento,
        alergias: existente.data.alergias,
        observaciones: existente.data.observaciones,
        pesoKg: null,
      }
    : VACIA;

  return (
    <Formulario
      key={mascotaID ?? "nueva"}
      inicial={inicial}
      mascotaID={mascotaID}
      catalogos={catalogos.data!}
    />
  );
}

type Props = {
  inicial: GuardarMascota;
  mascotaID?: string;
  catalogos: Catalogos;
};

function Formulario({ inicial, mascotaID, catalogos }: Props) {
  const esEdicion = Boolean(mascotaID);
  const navegar = useNavigate();
  const qc = useQueryClient();

  const [datos, setDatos] = useState<GuardarMascota>(inicial);
  const [errores, setErrores] = useState<Record<string, string[]>>({});

  const guardar = useMutation({
    mutationFn: (d: GuardarMascota) =>
      esEdicion
        ? actualizarMascota(mascotaID!, d).then(() => ({ mascotaID: mascotaID! }))
        : crearMascota(d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      qc.invalidateQueries({ queryKey: ["mascota", r.mascotaID] });
      navegar(`/mascotas/${r.mascotaID}`);
    },
    onError: (e) => setErrores(e instanceof ErrorApi ? e.errores : {}),
  });

  function set<K extends keyof GuardarMascota>(campo: K, valor: GuardarMascota[K]) {
    setDatos((d) => ({ ...d, [campo]: valor }));
  }

  // Al cambiar de especie, la raza elegida deja de tener sentido.
  function cambiarEspecie(especieID: number) {
    setDatos((d) => ({ ...d, especieID, razaID: null }));
  }

  const razas = catalogos.especies.find((e) => e.especieID === datos.especieID)?.razas ?? [];
  const esFallecida = datos.estado === 2;

  return (
    <>
      <div className="titulo-fila">
        <h1>{esEdicion ? `Editar ${inicial.nombre}` : "Nueva mascota"}</h1>
      </div>

      <form
        className="panel formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setErrores({});
          guardar.mutate(datos);
        }}
      >
        <Campo etiqueta="Nombre" errores={errores.nombre} requerido>
          <input
            value={datos.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            maxLength={100}
            required
            autoFocus
          />
        </Campo>

        <div className="fila">
          <Campo etiqueta="Especie" errores={errores.especieID} requerido>
            <select
              value={datos.especieID || ""}
              onChange={(e) => cambiarEspecie(Number(e.target.value))}
              required
            >
              <option value="">Elige…</option>
              {catalogos.especies.map((e) => (
                <option key={e.especieID} value={e.especieID}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Raza" errores={errores.razaID}>
            <select
              value={datos.razaID ?? ""}
              onChange={(e) => set("razaID", e.target.value ? Number(e.target.value) : null)}
              disabled={!datos.especieID}
            >
              <option value="">Sin especificar</option>
              {razas.map((r) => (
                <option key={r.razaID} value={r.razaID}>
                  {r.nombre}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div className="fila">
          <Campo etiqueta="Sexo" errores={errores.sexo}>
            <select value={datos.sexo} onChange={(e) => set("sexo", Number(e.target.value))}>
              {SEXO.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Color" errores={errores.colorID}>
            <select
              value={datos.colorID ?? ""}
              onChange={(e) => set("colorID", e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Sin especificar</option>
              {catalogos.colores.map((c) => (
                <option key={c.colorID} value={c.colorID}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div className="fila">
          <Campo etiqueta="Fecha de nacimiento" errores={errores.fechaNacimiento}>
            <input
              type="date"
              value={paraInputFecha(datos.fechaNacimiento)}
              onChange={(e) => set("fechaNacimiento", e.target.value || null)}
            />
          </Campo>

          <Campo etiqueta="Lugar de nacimiento" errores={errores.lugarNacimiento}>
            <input
              value={datos.lugarNacimiento ?? ""}
              onChange={(e) => set("lugarNacimiento", e.target.value || null)}
            />
          </Campo>
        </div>

        {!esEdicion && (
          <Campo
            etiqueta="Peso actual (kg)"
            errores={errores.pesoKg}
            ayuda="Se guarda como la primera medición. Después se registra en cada visita."
          >
            <input
              type="number"
              step="0.001"
              min="0"
              value={datos.pesoKg ?? ""}
              onChange={(e) => set("pesoKg", e.target.value ? Number(e.target.value) : null)}
            />
          </Campo>
        )}

        <Campo
          etiqueta="Número de chip"
          errores={errores.numeroChip}
          ayuda="Identifica a la mascota a nivel nacional. No puede repetirse."
        >
          <input
            value={datos.numeroChip ?? ""}
            onChange={(e) => set("numeroChip", e.target.value || null)}
            maxLength={50}
          />
        </Campo>

        <div className="fila">
          <Campo etiqueta="Esterilizada" errores={errores.esterilizada}>
            <select
              value={datos.esterilizada === null ? "" : String(datos.esterilizada)}
              onChange={(e) =>
                set("esterilizada", e.target.value === "" ? null : e.target.value === "true")
              }
            >
              <option value="">Sin dato</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </Campo>

          {datos.esterilizada === true && (
            <Campo etiqueta="Fecha de esterilización" errores={errores.fechaEsterilizacion}>
              <input
                type="date"
                value={paraInputFecha(datos.fechaEsterilizacion)}
                onChange={(e) => set("fechaEsterilizacion", e.target.value || null)}
              />
            </Campo>
          )}
        </div>

        {esEdicion && (
          <div className="fila">
            <Campo etiqueta="Estado" errores={errores.estado}>
              <select
                value={datos.estado ?? 1}
                onChange={(e) => {
                  const estado = Number(e.target.value);
                  setDatos((d) => ({
                    ...d,
                    estado,
                    // Cambiar a un estado que no es Fallecida limpia la fecha:
                    // el backend rechaza que cuenten historias distintas.
                    fechaFallecimiento: estado === 2 ? d.fechaFallecimiento : null,
                  }));
                }}
              >
                {ESTADO.map((s) => (
                  <option key={s.valor} value={s.valor}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </Campo>

            {esFallecida && (
              <Campo etiqueta="Fecha de fallecimiento" errores={errores.fechaFallecimiento}>
                <input
                  type="date"
                  value={paraInputFecha(datos.fechaFallecimiento)}
                  onChange={(e) => set("fechaFallecimiento", e.target.value || null)}
                />
              </Campo>
            )}
          </div>
        )}

        <Campo etiqueta="Alergias" errores={errores.alergias}>
          <textarea
            rows={2}
            value={datos.alergias ?? ""}
            onChange={(e) => set("alergias", e.target.value || null)}
          />
        </Campo>

        <Campo etiqueta="Observaciones" errores={errores.observaciones}>
          <textarea
            rows={3}
            value={datos.observaciones ?? ""}
            onChange={(e) => set("observaciones", e.target.value || null)}
          />
        </Campo>

        {guardar.error && Object.keys(errores).length === 0 && (
          <p className="err">{guardar.error.message}</p>
        )}

        <div className="acciones">
          <button type="submit" className="boton-primario" disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </button>
          <Link className="boton" to={esEdicion ? `/mascotas/${mascotaID}` : "/"}>
            Cancelar
          </Link>
        </div>
      </form>
    </>
  );
}

